import { Modal, Tree, type TreeDataNode as DataNode } from "antd";
import type { Key } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateDocumentNode } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import { EntityObject } from "../../../../common/types";
import { DocumentNodeType } from "../../enums";
import { CreateUpdateDocumentNode, DocumentNodeTree } from "../../types";

interface Props {
  onFormCancel: () => void;
  isOpen: boolean;
  dataNodes: DataNode[];
  nodesToMove?: DocumentNodeTree[];
}

const DocumentNodeMoveForm = ({ isOpen, onFormCancel, dataNodes, nodesToMove }: Props) => {
  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<Key | undefined>(dataNodes[0]?.key ?? undefined);

  const updateDocumentNode = useUpdateDocumentNode();

  const handleFormSubmit = async () => {
    let parentId: string | undefined;

    if (selectedKey) {
      parentId =
        selectedKey === DocumentNodeType.GENERAL || selectedKey === DocumentNodeType.PERSONAL
          ? undefined
          : selectedKey.toString();
    }

    const nodes = nodesToMove?.map<EntityObject<CreateUpdateDocumentNode>>((node) => ({
      id: node.id,
      object: {
        optimisticLockVersion: node.optimisticLockVersion,
        name: node.name,
        type: node.type,
        parentId: parentId,
      },
    }));

    if (nodes?.length) {
      await Promise.all(nodes.map((node) => updateDocumentNode.mutateAsync(node).then(onFormCancel)));
    }
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={isOpen}
      title={t("documentNode.titles.moveNodes")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={updateDocumentNode.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      destroyOnHidden
      mask={{ closable: false }}
      afterClose={() => setSelectedKey(dataNodes[0]?.key)}
    >
      <Tree.DirectoryTree
        treeData={dataNodes}
        defaultExpandedKeys={dataNodes[0]?.key ? [dataNodes[0]?.key] : undefined}
        expandAction="doubleClick"
        onSelect={(selectedKeys) => setSelectedKey(selectedKeys[0])}
        selectedKeys={selectedKey ? [selectedKey] : undefined}
      />
    </Modal>
  );
};

export default DocumentNodeMoveForm;
